import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDownIcon,
  ArrowPathIcon,
  ArrowUpIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  PencilSquareIcon,
  PhoneIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { FaDog } from 'react-icons/fa';
import apiClient from '../utils/apiClient';
import MemberAvatar from '../components/MemberAvatar';
import AttributeLabel from '../components/AttributeLabel';
import { StatCard, StatGroup } from '../components/StatCard';
import { ModalShell, ModalActions, ModalSection } from '../components/modals/core';

const DogIcon: React.FC<{ className?: string }> = ({ className }) => {
  const Icon = FaDog as React.ComponentType<{ className?: string }>;
  return <Icon className={className} />;
};

type Status =
  | 'neu'
  | 'gelesen'
  | 'kontaktiert'
  | 'warteliste'
  | 'aktiv'
  | 'abgelehnt'
  | 'abgemeldet'
  | 'spam';

type WaitlistMessage = {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  dog_name: string | null;
  dog_breed: string | null;
  message: string;
  ip_address: string | null;
  status: Status;
  notes: string | null;
  contacted_at: string | null;
  status_changed_at: string | null;
  status_changed_by: string | null;
  email_sent: number;
  email_error: string | null;
  unsubscribe_token: string | null;
  unsubscribed_at: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

type Stats = Record<Status, number> & { total: number };

const STATUS_META: Record<Status, { label: string; chip: string; dot: string; pillTone: string }> = {
  neu:         { label: 'Neu',          chip: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200', dot: 'bg-emerald-500', pillTone: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30' },
  gelesen:     { label: 'Gelesen',      chip: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',         dot: 'bg-blue-500',    pillTone: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30' },
  kontaktiert: { label: 'Kontaktiert',  chip: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200', dot: 'bg-indigo-500',  pillTone: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:border-indigo-500/30' },
  warteliste:  { label: 'Warteliste',   chip: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',     dot: 'bg-amber-500',   pillTone: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30' },
  aktiv:       { label: 'Aktiv',        chip: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',     dot: 'bg-green-500',   pillTone: 'bg-primary-50 text-primary-700 border-primary-200 dark:bg-primary-500/15 dark:text-primary-300 dark:border-primary-500/30' },
  abgelehnt:   { label: 'Abgelehnt',    chip: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',         dot: 'bg-rose-500',    pillTone: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500/30' },
  abgemeldet:  { label: 'Abgemeldet',   chip: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300',            dot: 'bg-gray-400',    pillTone: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-white/5 dark:text-slate-300 dark:border-white/15' },
  spam:        { label: 'Spam',         chip: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',             dot: 'bg-red-500',     pillTone: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30' },
};

const STATUS_ORDER: Status[] = ['neu', 'gelesen', 'kontaktiert', 'warteliste', 'aktiv', 'abgelehnt', 'abgemeldet', 'spam'];

const PAGE_SIZE = 25;

const MONTH_SHORT = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

// Email-templates with placeholder support: {{name}}, {{hund}}, {{rasse}}
type EmailTemplate = {
  id: string;
  label: string;
  desc: string;
  subject: string;
  body: string;
};

const EMAIL_TEMPLATES: EmailTemplate[] = [
  {
    id: 'willkommen',
    label: 'Willkommen',
    desc: 'Begrüßung · nächste Schritte · Termin-Vorschlag',
    subject: 'Willkommen bei der HSV — deine Bewerbung',
    body: `Liebe/r {{name}},

vielen Dank für deine Bewerbung bei der HSV. Wir freuen uns auf dich{{hund_zusatz}}.

Komm gerne mal Samstag um 10 Uhr unverbindlich vorbei — dann lernen wir uns alle in Ruhe kennen.

Liebe Grüße
Vorstand HSV`,
  },
  {
    id: 'rueckfrage',
    label: 'Rückfrage',
    desc: 'Mehr Infos zum Hund / zur Verfügbarkeit',
    subject: 'Rückfrage zu deiner Bewerbung — HSV',
    body: `Hallo {{name}},

danke für deine Anfrage. Damit wir dich gut einordnen können, hätten wir noch ein paar Rückfragen{{hund_zusatz}}:

· Wie alt ist dein Hund genau?
· Hast du schon Hundeschul-Erfahrung?
· An welchen Wochentagen hättest du Zeit?

Liebe Grüße
Vorstand HSV`,
  },
  {
    id: 'warteliste',
    label: 'Auf Warteliste',
    desc: 'Eingang bestätigen · ETA grob',
    subject: 'Du bist auf der Warteliste — HSV',
    body: `Hallo {{name}},

deine Anfrage haben wir erhalten und auf die Warteliste gesetzt. Aktuell sind unsere Kurse voll — sobald ein Platz frei wird, melden wir uns sofort.

Erfahrungsgemäß dauert das 4–8 Wochen.

Liebe Grüße
Vorstand HSV`,
  },
  {
    id: 'ablehnung',
    label: 'Höfliche Ablehnung',
    desc: 'Volle Plätze · Empfehlung anderer Vereine',
    subject: 'Deine Bewerbung bei der HSV',
    body: `Hallo {{name}},

leider können wir dich aktuell nicht aufnehmen — alle Kurse sind voll und auch unsere Warteliste ist sehr lang.

Wir empfehlen dir, dich bei benachbarten Hundesportvereinen umzuschauen.

Alles Gute für dich{{hund_zusatz}}!

Liebe Grüße
Vorstand HSV`,
  },
];

function formatDate(iso: string | null): string {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatLongDate(iso: string | null): string {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
}

function buildDogInfo(m: WaitlistMessage): string {
  return [m.dog_name, m.dog_breed].filter(Boolean).join(' · ');
}

function daysSince(iso: string | null): number {
  if (!iso) return 0;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 0;
  const ms = Date.now() - d.getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
}

// Warteliste-Einträge haben nur ein einzelnes `name`-Feld (kein vorname/name-Split).
// Für MemberAvatar (baut Initialen aus vorname[0]+name[0]) den vollen Namen aufteilen.
function splitName(full: string): { vorname: string; name: string } {
  const parts = (full || '').trim().split(/\s+/);
  return { vorname: parts[0] || '', name: parts.slice(1).join(' ') };
}

type DateTileVariant = 'amber' | 'emerald' | 'primary';

function dateTileVariant(days: number): DateTileVariant {
  if (days > 21) return 'amber';
  if (days <= 7) return 'emerald';
  return 'primary';
}

const DATE_TILE_STYLES: Record<DateTileVariant, { wrap: string; kicker: string; tail: string }> = {
  amber: {
    wrap: 'border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10',
    kicker: 'text-amber-600 dark:text-amber-300',
    tail: 'text-amber-500 dark:text-amber-300/80',
  },
  emerald: {
    wrap: 'border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10',
    kicker: 'text-emerald-600 dark:text-emerald-300',
    tail: 'text-emerald-500 dark:text-emerald-300/80',
  },
  primary: {
    wrap: 'border-primary-200 bg-primary-50 dark:border-primary-500/30 dark:bg-primary-500/10',
    kicker: 'text-primary-600 dark:text-primary-300',
    tail: 'text-primary-500 dark:text-primary-300/80',
  },
};

const DateTile: React.FC<{ iso: string | null }> = ({ iso }) => {
  const days = daysSince(iso);
  const variant = dateTileVariant(days);
  const styles = DATE_TILE_STYLES[variant];
  const d = iso ? new Date(iso) : null;
  const validDate = d && !Number.isNaN(d.getTime());
  const month = validDate ? MONTH_SHORT[d.getMonth()] : '—';
  const day = validDate ? d.getDate().toString().padStart(2, '0') : '—';

  let tailLabel: string;
  if (days === 0) tailLabel = 'Heute';
  else if (days === 1) tailLabel = '1 Tg';
  else tailLabel = `${days} Tg`;

  return (
    <div
      className={`shrink-0 flex flex-col items-center justify-center rounded-2xl border ${styles.wrap}`}
      style={{ width: 64, height: 76 }}
    >
      <div className={`text-[9px] uppercase tracking-widest font-bold ${styles.kicker}`}>{month}</div>
      <div className="text-2xl font-black text-gray-900 dark:text-white tabular-nums leading-none">{day}</div>
      <div className={`text-[9px] font-semibold mt-0.5 ${styles.tail}`}>{tailLabel}</div>
    </div>
  );
};

const WaitlistManagement: React.FC = () => {
  const [items, setItems] = useState<WaitlistMessage[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<Status | 'alle'>('alle');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const [sortMode, setSortMode] = useState<'oldest' | 'newest' | 'status'>('oldest');

  const [selected, setSelected] = useState<WaitlistMessage | null>(null);
  const [emailTarget, setEmailTarget] = useState<WaitlistMessage | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      };
      if (statusFilter !== 'alle') params.status = statusFilter;
      if (search.trim()) params.search = search.trim();

      const res = await apiClient.get('/api/waitlist', { params });
      if (res.data && res.data.success) {
        setItems(res.data.messages || []);
        setTotal(res.data.total || 0);
        setStats(res.data.stats || null);
      } else {
        setError(res.data?.message || 'Fehler beim Laden');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Netzwerkfehler');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Suche debouncen (300ms)
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleStatusFilter = (s: Status | 'alle') => {
    setStatusFilter(s);
    setPage(0);
  };

  const refreshKeepingSelection = async (id?: number) => {
    await fetchData();
    if (id != null) {
      try {
        const res = await apiClient.get(`/api/waitlist/${id}`);
        if (res.data?.success) setSelected(res.data.message);
      } catch {
        /* ignore */
      }
    }
  };

  const handleMove = async (id: number, dir: 'up' | 'down') => {
    try {
      await apiClient.post(`/api/waitlist/${id}/move-${dir}`);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Sortieren fehlgeschlagen');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Diesen Eintrag wirklich löschen?')) return;
    try {
      await apiClient.delete(`/api/waitlist/${id}`);
      setSelected(null);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Löschen fehlgeschlagen');
    }
  };

  // Quick approve / reject from list (status update)
  const quickStatus = async (id: number, newStatus: Status) => {
    try {
      await apiClient.patch(`/api/waitlist/${id}`, { status: newStatus });
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Status-Änderung fehlgeschlagen');
    }
  };

  const visibleStats = useMemo(() => {
    if (!stats) return [];
    return STATUS_ORDER.map(s => ({ status: s, count: stats[s] || 0 }));
  }, [stats]);

  // Sorted items based on sortMode (client-side ordering, server returns by sort_order)
  const sortedItems = useMemo(() => {
    const arr = [...items];
    if (sortMode === 'oldest') {
      arr.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    } else if (sortMode === 'newest') {
      arr.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    } else if (sortMode === 'status') {
      const ord = (s: Status) => STATUS_ORDER.indexOf(s);
      arr.sort((a, b) => ord(a.status) - ord(b.status));
    }
    return arr;
  }, [items, sortMode]);

  // Story metrics for intro
  const storyMetrics = useMemo(() => {
    const open = (stats?.neu || 0) + (stats?.gelesen || 0);
    const thisWeek = items.filter(m => daysSince(m.created_at) <= 7).length;
    const oldest = items.reduce((max, m) => Math.max(max, daysSince(m.created_at)), 0);
    const withDog = items.filter(m => !!(m.dog_name || m.dog_breed)).length;
    const trainerInterest = items.filter(m =>
      (m.message || '').toLowerCase().match(/\btrainer|co-trainer|assistent\b/)
    ).length;
    return { open, thisWeek, oldest, withDog, trainerInterest };
  }, [stats, items]);

  return (
    <div className="font-sans text-gray-800 dark:text-slate-200">
      <div className="max-w-7xl mx-auto">

        {/* Header - konform zum Standard-Muster (Icon + Titel + Untertitel + Akzentlinie) */}
        <header className="app-page-header hidden sm:block bg-white dark:bg-gray-800 border-b-[3px] border-lime-400 mb-6">
          <div className="p-4 md:py-6 md:px-8 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <ClipboardDocumentListIcon className="h-7 w-7 text-primary-600 dark:text-primary-400" />
              <div>
                <h1 className="text-[28px] font-bold text-gray-900 dark:text-white">Warteliste</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Anfragen aus dem Kontaktformular verwalten</p>
              </div>
            </div>
            <button
              onClick={() => { void fetchData(); }}
              className="flex items-center gap-2 h-10 md:h-11 px-4 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium transition-colors flex-shrink-0"
            >
              <ArrowPathIcon className="h-4 w-4" />
              <span className="hidden md:inline">Aktualisieren</span>
            </button>
          </div>
        </header>

        {/* Mobile Header */}
        <header className="app-mobile-page-header sm:hidden bg-white dark:bg-gray-800 px-4 py-3 border-b-[3px] border-lime-400 mb-4 flex items-center gap-3">
          <ClipboardDocumentListIcon className="h-6 w-6 text-primary-600 dark:text-primary-400 flex-shrink-0" />
          <h1 className="text-lg font-bold text-gray-900 dark:text-white">Warteliste</h1>
        </header>

      <div className="pb-8 sm:pb-10">

        {/* Kennzahlen */}
        <section className="pt-2 pb-6">
          <StatGroup columns={3}>
            <StatCard
              icon={ExclamationTriangleIcon}
              tone="warning"
              valueTone="warning"
              label="Offen"
              value={storyMetrics.open}
              sub="unbearbeitet"
            />
            <StatCard
              icon={EnvelopeIcon}
              tone="neutral"
              label="Diese Woche"
              value={storyMetrics.thisWeek}
              sub="Eingänge"
            />
            <StatCard
              icon={ClipboardDocumentListIcon}
              tone="primary"
              valueTone="primary"
              label="Gesamt"
              value={stats?.total ?? total}
              sub="alle Status"
            />
          </StatGroup>
        </section>

        {/* Error Banner */}
        {error && (
          <section className="pb-6">
            <div className="rounded-2xl px-4 py-3 flex items-start gap-3 border border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10">
              <span className="w-9 h-9 rounded-xl bg-amber-100 border border-amber-200 text-amber-700 dark:bg-amber-500/15 dark:border-amber-500/30 dark:text-amber-300 grid place-items-center flex-shrink-0">
                <ExclamationTriangleIcon className="w-5 h-5" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">Daten konnten nicht geladen werden</p>
                <p className="text-xs text-amber-600 dark:text-amber-300/80 mt-0.5">{error}</p>
              </div>
              <button
                onClick={() => fetchData()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-white text-xs font-bold transition"
              >
                <ArrowPathIcon className="w-3.5 h-3.5" />
                Erneut
              </button>
            </div>
          </section>
        )}


        {/* FILTER TOOLBAR */}
        <section className="pb-6">
          <div
            className="rounded-2xl p-4 md:p-5 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-gray-700"
          >
            <div className="flex flex-wrap items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 min-w-[240px]">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Name, E-Mail, Hund, Notiz …"
                  className="w-full h-11 pl-9 pr-3 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 transition"
                />
              </div>
              {/* Sort - Segmented Control */}
              <div className="flex items-center gap-2">
                <AttributeLabel
                  iconKey="sort"
                  size="sm"
                  className="hidden sm:inline-flex"
                  textClassName="text-xs text-gray-500 dark:text-gray-400"
                >
                  Sortierung:
                </AttributeLabel>
                <div className="flex rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                  {([
                    { id: 'oldest' as const, label: 'Älteste' },
                    { id: 'newest' as const, label: 'Neueste' },
                    { id: 'status' as const, label: 'Status' },
                  ]).map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => setSortMode(opt.id)}
                      className={`px-3 py-2 text-xs font-semibold transition-colors ${
                        sortMode === opt.id
                          ? 'bg-primary-600 text-white'
                          : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Filter pills */}
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => handleStatusFilter('alle')}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  statusFilter === 'alle'
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                Alle {stats ? <span className="opacity-60">({stats.total})</span> : null}
              </button>
              {STATUS_ORDER.map(s => {
                const meta = STATUS_META[s];
                const active = statusFilter === s;
                return (
                  <button
                    key={s}
                    onClick={() => handleStatusFilter(s)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border inline-flex items-center gap-1.5 transition-colors ${
                      active
                        ? 'bg-primary-600 border-primary-600 text-white'
                        : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                    {meta.label}
                    {stats ? <span className="opacity-60">({stats[s] || 0})</span> : null}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* APPLICANT LIST */}
        <section className="pb-6">
          <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-[#1c2432] sm:p-6 sm:shadow-md">
          <div className="mb-3 flex flex-col items-start justify-between gap-2 sm:mb-4 sm:flex-row sm:items-center sm:gap-4">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white sm:text-lg">
              Wer wartet ({total})
            </h2>
            <div className="text-xs text-gray-500 dark:text-gray-400 hidden md:block">
              Sortiert nach{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                {sortMode === 'oldest' ? 'Eingangsdatum (älteste)' : sortMode === 'newest' ? 'Eingangsdatum (neueste)' : 'Status'}
              </span>
            </div>
          </div>

          {loading && items.length === 0 ? (
            <div className="rounded-2xl p-10 text-center text-gray-600 dark:text-slate-400 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700">
              Wird geladen …
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl p-8 md:p-12 text-center bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700">
              <div className="w-16 h-16 rounded-2xl bg-primary-100 dark:bg-primary-900/30 mx-auto mb-4 flex items-center justify-center">
                <ClipboardDocumentListIcon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Keine Einträge</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md mx-auto">
                Aktuell liegen keine Anfragen aus dem Kontaktformular vor.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedItems.map((m, idx) => {
                const meta = STATUS_META[m.status];
                const dog = buildDogInfo(m);
                const days = daysSince(m.created_at);
                const isOldest = days > 21;
                const isToday = days === 0;
                const borderClass = isOldest
                  ? 'border-amber-300 dark:border-amber-500/30'
                  : isToday
                  ? 'border-emerald-300 dark:border-emerald-500/30'
                  : 'border-gray-200 dark:border-gray-700';

                return (
                  <article
                    key={m.id}
                    className={`bg-gray-50 dark:bg-gray-900/40 rounded-2xl p-4 md:p-5 transition-colors hover:bg-gray-100 dark:hover:bg-white/[0.03] border ${borderClass}`}
                  >
                    <div className="flex items-start gap-3 md:gap-4">
                      {/* Sort buttons (mobile-hidden) */}
                      <div className="hidden md:flex flex-col gap-1 pt-1">
                        <button
                          onClick={() => handleMove(m.id, 'up')}
                          disabled={idx === 0 && page === 0}
                          title="Nach oben"
                          className="w-7 h-7 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                          <ArrowUpIcon className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleMove(m.id, 'down')}
                          title="Nach unten"
                          className="w-7 h-7 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-white/10 flex items-center justify-center"
                        >
                          <ArrowDownIcon className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      {/* Date Tile */}
                      <DateTile iso={m.created_at} />

                      {/* Avatar */}
                      <MemberAvatar
                        member={splitName(m.name)}
                        size="md"
                        className="hidden sm:block"
                      />

                      {/* Content */}
                      <button
                        onClick={() => setSelected(m)}
                        className="flex-1 text-left min-w-0"
                      >
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="text-base font-black text-gray-900 dark:text-white truncate">{m.name}</span>
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${meta.pillTone}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot} ${m.status === 'neu' ? 'animate-pulse' : ''}`} />
                            {meta.label}
                          </span>
                          {isOldest && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border border-amber-500/30 bg-amber-500/15 text-amber-700 dark:text-amber-300">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                              {days} Tage offen
                            </span>
                          )}
                          {isToday && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                              Heute eingegangen
                            </span>
                          )}
                          {dog && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border border-blue-500/30 bg-blue-500/15 text-blue-700 dark:text-blue-300">
                              <DogIcon className="w-3 h-3" />
                              Mit Hund
                            </span>
                          )}
                          {m.email_sent === 0 && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border border-orange-500/30 bg-orange-500/15 text-orange-300">
                              E-Mail-Fehler
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-slate-400 flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                          <span className="inline-flex items-center gap-1.5">
                            <EnvelopeIcon className="w-3.5 h-3.5" />
                            {m.email}
                          </span>
                          {m.phone && (
                            <span className="inline-flex items-center gap-1.5">
                              <PhoneIcon className="w-3.5 h-3.5" />
                              {m.phone}
                            </span>
                          )}
                          {dog && (
                            <span className="inline-flex items-center gap-1.5">
                              <DogIcon className="w-3.5 h-3.5" />
                              {dog}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-700 dark:text-slate-300 mt-2 line-clamp-2">{m.message}</div>
                        <div className="mt-1.5 text-[11px] text-gray-500 dark:text-slate-500">
                          Eingegangen: {formatDate(m.created_at)}
                          {m.status_changed_by && ` · Geändert von ${m.status_changed_by}`}
                        </div>
                      </button>

                      {/* Actions (desktop) */}
                      <div className="hidden md:flex flex-col gap-2 shrink-0">
                        <button
                          onClick={() => setSelected(m)}
                          className="px-3 py-2 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-800 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-white/10 text-[11px] font-semibold transition inline-flex items-center justify-center gap-1.5"
                        >
                          <EyeIcon className="w-3.5 h-3.5" />
                          Details
                        </button>
                        <div className="flex gap-1.5">
                          <button
                            onClick={() => quickStatus(m.id, 'aktiv')}
                            className="w-9 h-9 rounded-xl bg-primary-500/10 border border-primary-500/30 text-primary-700 dark:text-primary-300 hover:bg-primary-500/20 transition flex items-center justify-center"
                            title="Annehmen"
                          >
                            <CheckCircleIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEmailTarget(m)}
                            className="w-9 h-9 rounded-xl bg-blue-500/10 border border-blue-500/30 text-blue-700 dark:text-blue-300 hover:bg-blue-500/20 transition flex items-center justify-center"
                            title="Kontaktieren"
                          >
                            <EnvelopeIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => quickStatus(m.id, 'abgelehnt')}
                            className="w-9 h-9 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-300 hover:bg-rose-500/20 transition flex items-center justify-center"
                            title="Ablehnen"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(m.id)}
                            className="w-9 h-9 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-slate-400 hover:bg-rose-500/10 hover:text-rose-300 hover:border-rose-500/30 transition flex items-center justify-center"
                            title="Löschen"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Actions (mobile) */}
                      <div className="md:hidden flex flex-col gap-1.5 shrink-0">
                        <button
                          onClick={() => setSelected(m)}
                          className="w-9 h-9 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-800 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-white/10 transition flex items-center justify-center"
                          title="Details"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(m.id)}
                          className="w-9 h-9 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-300 hover:bg-rose-500/20 transition flex items-center justify-center"
                          title="Löschen"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between text-xs text-gray-600 dark:text-slate-400">
              <span>
                Seite <span className="text-gray-900 dark:text-white font-bold">{page + 1}</span> von {totalPages} · {total}{' '}
                {total === 1 ? 'Eintrag' : 'Einträge'}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-2 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-800 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-white/10 inline-flex items-center gap-1 font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="w-3.5 h-3.5" />
                  Zurück
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-3 py-2 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-800 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-white/10 inline-flex items-center gap-1 font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Weiter
                  <ChevronRightIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
          </div>
        </section>

        {/* STAT GRID — full status breakdown */}
        {visibleStats.length > 0 && (
          <section className="pb-6">
            <div className="text-[11px] uppercase tracking-[0.22em] text-primary-600 dark:text-primary-400/80 font-semibold mb-3">
              Verteilung nach Status
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-2">
              {visibleStats.map(({ status, count }) => {
                const meta = STATUS_META[status];
                const active = statusFilter === status;
                return (
                  <button
                    key={status}
                    onClick={() => handleStatusFilter(active ? 'alle' : status)}
                    className={`text-left rounded-2xl p-3 border transition ${
                      active
                        ? 'bg-primary-500/15 border-primary-500/40'
                        : 'bg-white dark:bg-[#1c2432] border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-white/[0.03]'
                    }`}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                      <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-semibold truncate">
                        {meta.label}
                      </span>
                    </div>
                    <div className={`text-2xl font-black tabular-nums ${active ? 'text-primary-600 dark:text-primary-300' : 'text-gray-900 dark:text-white'}`}>
                      {count}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Detail Modal */}
        {selected && (
          <DetailModal
            message={selected}
            onClose={() => setSelected(null)}
            onChange={(id) => refreshKeepingSelection(id)}
            onDelete={() => handleDelete(selected.id)}
            onContact={() => setEmailTarget(selected)}
          />
        )}

        {/* Email-Template Modal */}
        {emailTarget && (
          <EmailTemplateModal
            message={emailTarget}
            onClose={() => setEmailTarget(null)}
            onSent={() => {
              setEmailTarget(null);
              fetchData();
            }}
          />
        )}
      </div>
      </div>
    </div>
  );
};

// ---------- Detail Modal ----------

interface DetailProps {
  message: WaitlistMessage;
  onClose: () => void;
  onChange: (id: number) => void;
  onDelete: () => void;
  onContact: () => void;
}

const DetailModal: React.FC<DetailProps> = ({ message, onClose, onChange, onDelete, onContact }) => {
  const [status, setStatus] = useState<Status>(message.status);
  const [notes, setNotes] = useState<string>(message.notes || '');
  const [contactedAt, setContactedAt] = useState<string>(
    message.contacted_at ? message.contacted_at.replace(' ', 'T').slice(0, 16) : ''
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setStatus(message.status);
    setNotes(message.notes || '');
    setContactedAt(message.contacted_at ? message.contacted_at.replace(' ', 'T').slice(0, 16) : '');
  }, [message]);

  const handleSave = async () => {
    setSaving(true);
    setErr(null);
    try {
      const payload: any = { status, notes };
      if (contactedAt) {
        payload.contactedAt = contactedAt.replace('T', ' ');
      } else {
        payload.contactedAt = null;
      }
      await apiClient.patch(`/api/waitlist/${message.id}`, payload);
      onChange(message.id);
    } catch (e: any) {
      setErr(e.response?.data?.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const dog = buildDogInfo(message);
  const days = daysSince(message.created_at);
  const statusMeta = STATUS_META[message.status];

  return (
    <ModalShell
      open
      onClose={() => onClose()}
      variant="form"
      size="md"
      title={message.name}
      subtitle={
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${statusMeta.pillTone}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${statusMeta.dot}`} />
            {statusMeta.label}
          </span>
          <span>
            {days} {days === 1 ? 'Tag' : 'Tage'} offen · Eingegangen {formatDate(message.created_at)}
          </span>
        </span>
      }
      closeOnBackdrop={false}
      footer={
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <button
            onClick={onContact}
            className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-blue-500/10 border border-blue-500/30 text-blue-700 dark:text-blue-300 hover:bg-blue-500/20 transition-colors"
            title="Kontaktieren"
            aria-label="Kontaktieren"
          >
            <EnvelopeIcon className="w-5 h-5" />
          </button>
          <button
            onClick={onDelete}
            className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-300 hover:bg-rose-500/20 transition-colors"
            title="Löschen"
            aria-label="Löschen"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
          <div className="min-w-0 flex-1">
            <ModalActions
              secondary={{ label: 'Abbrechen', onClick: () => onClose() }}
              primary={{ label: 'Speichern', onClick: handleSave, loading: saving }}
            />
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Stammdaten */}
        <ModalSection title="Stammdaten" actions={<MemberAvatar member={splitName(message.name)} size="sm" />}>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-900/40">
              <AttributeLabel
                iconKey="email"
                size="sm"
                textClassName="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500"
              >
                E-Mail
              </AttributeLabel>
              <a
                href={`mailto:${message.email}`}
                className="block text-sm font-bold text-primary-700 dark:text-primary-300 hover:underline mt-0.5 truncate"
              >
                {message.email}
              </a>
            </div>
            <div className="px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-900/40">
              <AttributeLabel
                iconKey="phone"
                size="sm"
                textClassName="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500"
              >
                Telefon
              </AttributeLabel>
              {message.phone ? (
                <a
                  href={`tel:${message.phone}`}
                  className="block text-sm font-bold text-gray-900 dark:text-white hover:text-primary-300 mt-0.5 truncate"
                >
                  {message.phone}
                </a>
              ) : (
                <div className="text-sm font-bold text-gray-500 dark:text-slate-500 mt-0.5">–</div>
              )}
            </div>
            <div className="px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-900/40">
              <AttributeLabel
                iconKey="dog"
                size="sm"
                textClassName="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500"
              >
                Hund
              </AttributeLabel>
              <div className="text-sm font-bold text-gray-900 dark:text-white mt-0.5 truncate">{dog || '–'}</div>
            </div>
            <div className="px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-900/40">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">IP-Adresse</div>
              <div className="text-sm font-mono text-gray-900 dark:text-white mt-0.5 truncate">{message.ip_address || '–'}</div>
            </div>
          </div>
        </ModalSection>

        {/* Nachricht */}
        <ModalSection title="Nachricht der Bewerberin">
          <div className="rounded-xl border border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-900/40 p-4 text-sm text-gray-800 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">
            {message.message}
          </div>
        </ModalSection>

        {/* E-Mail-Status / unsubscribed */}
        {message.email_sent === 0 && message.email_error && (
          <div className="rounded-xl border border-orange-500/30 bg-orange-500/10 p-3 text-sm text-orange-200">
            <div className="font-semibold mb-1">E-Mail-Versand fehlgeschlagen:</div>
            <div className="text-xs font-mono break-all text-orange-300/80">{message.email_error}</div>
          </div>
        )}
        {message.unsubscribed_at && (
          <div className="rounded-xl border border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-900/40 p-3 text-sm text-gray-700 dark:text-slate-300">
            Hat sich am {formatDate(message.unsubscribed_at)} selbst abgemeldet.
          </div>
        )}

        {/* Status setzen */}
        <ModalSection title="Status setzen">
          <div className="flex flex-wrap gap-2">
            {STATUS_ORDER.map(s => {
              const meta = STATUS_META[s];
              const active = status === s;
              return (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border inline-flex items-center gap-1.5 transition ${
                    active
                      ? 'bg-primary-500 border-primary-500 text-white'
                      : 'bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-white/10'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                  {meta.label}
                </button>
              );
            })}
          </div>
          {message.status_changed_at && (
            <p className="mt-2 text-xs text-gray-500 dark:text-slate-500">
              Zuletzt geändert {formatDate(message.status_changed_at)}
              {message.status_changed_by ? ` von ${message.status_changed_by}` : ''}
            </p>
          )}
        </ModalSection>

        {/* Kontaktiert am */}
        <ModalSection title="Kontaktiert am">
          <input
            type="datetime-local"
            value={contactedAt}
            onChange={(e) => setContactedAt(e.target.value)}
            className="w-full sm:w-72 px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700 text-sm text-gray-900 dark:text-white outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 transition [color-scheme:light] dark:[color-scheme:dark]"
          />
        </ModalSection>

        {/* Notizen */}
        <ModalSection title="Interne Notizen">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            placeholder="z.B. Telefonat geführt, Termin vereinbart, Sonderwünsche …"
            className="w-full px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 transition resize-y"
          />
        </ModalSection>

        {err && (
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
            {err}
          </div>
        )}
      </div>
    </ModalShell>
  );
};

// ---------- Email Template Modal ----------

interface EmailTemplateProps {
  message: WaitlistMessage;
  onClose: () => void;
  onSent: () => void;
}

const EmailTemplateModal: React.FC<EmailTemplateProps> = ({ message, onClose, onSent }) => {
  const [templateId, setTemplateId] = useState<string>(EMAIL_TEMPLATES[0].id);
  const [subject, setSubject] = useState<string>('');
  const [body, setBody] = useState<string>('');
  const [editing, setEditing] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const replacePlaceholders = useCallback((text: string): string => {
    const firstName = (message.name || '').trim().split(/\s+/)[0] || message.name || '';
    const dogStr = buildDogInfo(message);
    const hundZusatz = dogStr ? ` und ${message.dog_name || 'deinen Hund'}` : '';
    return text
      .replace(/\{\{name\}\}/g, firstName)
      .replace(/\{\{full_name\}\}/g, message.name || '')
      .replace(/\{\{hund\}\}/g, message.dog_name || '–')
      .replace(/\{\{rasse\}\}/g, message.dog_breed || '–')
      .replace(/\{\{hund_zusatz\}\}/g, hundZusatz)
      .replace(/\{\{email\}\}/g, message.email || '');
  }, [message]);

  // Apply selected template
  useEffect(() => {
    const tpl = EMAIL_TEMPLATES.find(t => t.id === templateId) || EMAIL_TEMPLATES[0];
    setSubject(replacePlaceholders(tpl.subject));
    setBody(replacePlaceholders(tpl.body));
    setEditing(false);
  }, [templateId, replacePlaceholders]);

  const handleSend = async () => {
    setSending(true);
    setErr(null);
    try {
      // Try sending email via potential endpoint; gracefully fallback to status-update
      try {
        await apiClient.post(`/api/waitlist/${message.id}/send-email`, {
          subject,
          body,
          template: templateId,
        });
      } catch (sendErr: any) {
        // Endpoint may not exist; we still flag as kontaktiert
        if (sendErr.response?.status !== 404) throw sendErr;
      }
      const now = new Date();
      const iso = now.toISOString().slice(0, 19).replace('T', ' ');
      await apiClient.patch(`/api/waitlist/${message.id}`, {
        status: 'kontaktiert',
        contactedAt: iso,
      });
      onSent();
    } catch (e: any) {
      setErr(e.response?.data?.message || 'Senden fehlgeschlagen');
    } finally {
      setSending(false);
    }
  };

  // Render preview body with highlighted placeholders (replaced values)
  const renderHighlightedPreview = (text: string): React.ReactNode => {
    // Re-derive replacements so we can wrap them visually
    const firstName = (message.name || '').trim().split(/\s+/)[0] || message.name || '';
    const replacements: Array<[string, string]> = [
      ['{{name}}', firstName],
      ['{{full_name}}', message.name || ''],
      ['{{hund}}', message.dog_name || '–'],
      ['{{rasse}}', message.dog_breed || '–'],
      ['{{email}}', message.email || ''],
    ];
    // Build a regex of non-empty replacement values, escape regex
    const escapes = replacements
      .map(([, v]) => v)
      .filter(v => v && v !== '–')
      .map(v => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    if (escapes.length === 0) {
      return text.split('\n').map((line, i) => (
        <p key={i}>{line || ' '}</p>
      ));
    }
    const re = new RegExp(`(${escapes.join('|')})`, 'g');
    return text.split('\n').map((line, i) => {
      const parts = line.split(re);
      return (
        <p key={i}>
          {parts.length === 0 || (parts.length === 1 && parts[0] === '')
            ? ' '
            : parts.map((p, j) =>
                escapes.some(e => new RegExp(`^${e}$`).test(p)) ? (
                  <span
                    key={j}
                    className="bg-primary-500/15 text-primary-700 dark:text-primary-300 font-bold px-1 rounded"
                  >
                    {p}
                  </span>
                ) : (
                  <React.Fragment key={j}>{p}</React.Fragment>
                )
              )}
        </p>
      );
    });
  };

  return (
    <ModalShell
      open
      onClose={() => onClose()}
      variant="form"
      size="lg"
      title={`E-Mail an ${message.name}`}
      subtitle={message.email}
      closeOnBackdrop={false}
      footer={
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <button
            onClick={() => setEditing(e => !e)}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-800 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-white/10 text-xs font-semibold transition"
          >
            <PencilSquareIcon className="w-3.5 h-3.5" />
            {editing ? 'Vorschau' : 'Bearbeiten'}
          </button>
          <div className="min-w-0 flex-1">
            <ModalActions
              secondary={{ label: 'Abbrechen', onClick: () => onClose() }}
              primary={{
                label: 'Senden',
                onClick: handleSend,
                loading: sending,
                icon: <PaperAirplaneIcon className="h-4 w-4" />,
              }}
            />
          </div>
        </div>
      }
    >
      <div className="grid md:grid-cols-[1fr_1.6fr] gap-6">
        {/* Templates */}
        <ModalSection title="Vorlage auswählen">
          <div className="space-y-2">
            {EMAIL_TEMPLATES.map(tpl => {
              const active = templateId === tpl.id;
              return (
                <button
                  key={tpl.id}
                  onClick={() => setTemplateId(tpl.id)}
                  className={`w-full text-left px-4 py-3 rounded-2xl border transition ${
                    active
                      ? 'bg-primary-500/10 border-primary-500/40'
                      : 'bg-white/5 border-white/10 hover:bg-gray-100 dark:hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className={`text-sm font-black ${active ? 'text-white' : 'text-white'}`}>{tpl.label}</div>
                    {active && <CheckCircleIcon className="w-4 h-4 text-primary-700 dark:text-primary-300" />}
                  </div>
                  <div className="text-[11px] text-gray-600 dark:text-slate-400">{tpl.desc}</div>
                </button>
              );
            })}
          </div>

          <div className="mt-5 px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-900/40">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">
              Platzhalter ersetzen
            </div>
            <div className="mt-2 space-y-1.5 text-[11px] font-mono text-gray-700 dark:text-slate-300">
              <div>
                <span className="text-primary-700 dark:text-primary-300">{'{{name}}'}</span> →{' '}
                {(message.name || '').trim().split(/\s+/)[0] || '–'}
              </div>
              <div>
                <span className="text-primary-700 dark:text-primary-300">{'{{hund}}'}</span> → {message.dog_name || '–'}
              </div>
              <div>
                <span className="text-primary-700 dark:text-primary-300">{'{{rasse}}'}</span> → {message.dog_breed || '–'}
              </div>
              <div>
                <span className="text-primary-700 dark:text-primary-300">{'{{email}}'}</span> → {message.email}
              </div>
            </div>
          </div>
        </ModalSection>

        {/* Vorschau */}
        <ModalSection
          title="Vorschau"
          className="min-w-0"
          actions={
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Live-Vorschau
            </span>
          }
        >
          <div className="rounded-2xl border border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-900/40 overflow-hidden">
            {/* Mail header */}
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-100 dark:border-slate-800 dark:bg-slate-900/60 text-[11px] text-gray-600 dark:text-slate-400 space-y-1">
              <div>
                <span className="text-gray-500 dark:text-slate-500">An:</span>{' '}
                <span className="text-gray-900 dark:text-white font-bold">{message.email}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-slate-500">Von:</span>{' '}
                <span className="text-gray-900 dark:text-white">vorstand@mein-hundesportverein.de</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-slate-500">Betreff:</span>{' '}
                {editing ? (
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="ml-1 px-2 py-1 rounded-lg bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white text-[11px] outline-none focus:border-primary-500 w-[80%]"
                  />
                ) : (
                  <span className="text-gray-900 dark:text-white font-bold">{subject}</span>
                )}
              </div>
            </div>
            <div className="p-5 text-sm text-gray-800 dark:text-slate-200 leading-relaxed space-y-3">
              {editing ? (
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={12}
                  className="w-full px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700 text-sm text-gray-900 dark:text-white outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 transition resize-y"
                />
              ) : (
                renderHighlightedPreview(body)
              )}
            </div>
          </div>

          {err && (
            <div className="mt-3 rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
              {err}
            </div>
          )}
        </ModalSection>
      </div>
    </ModalShell>
  );
};

export default WaitlistManagement;
